import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { DraggableModal } from "@/components/ui/draggable-modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DatePicker } from "@/components/ui/DatePicker";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Calendar as CalendarIcon, Upload, X, FileText, AlertTriangle, Users, ChevronDown, Check, TreePine } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getNextWorkday, formatTRDate } from "@/utils/dateUtils";
import { evaluateSubstitutionLeaveConflict, annotateSubstituteEligibility } from "@/utils/leaveUtils";
import apiClient from "../api/client";

const LeaveRequestFormDialog = ({
  open,
  onOpenChange,
  leaveRequestForm,
  setLeaveRequestForm,
  handleSubmit,
  loading,
  isReadOnly = false,
  errorMessage,
  calculateLeaveDays,
  currentPerson,
  personnel = [],
  parks = [],
  asSubstitutePeriods = []
}) => {
  // LOCAL STATE - Performans için form değerleri yerel tutulur
  const [localForm, setLocalForm] = useState(() => ({
    start_date: leaveRequestForm.start_date || "",
    end_date: leaveRequestForm.end_date || "",
    return_date: leaveRequestForm.return_date || "",
    reason_type: leaveRequestForm.reason_type || "",
    reason_detail: leaveRequestForm.reason_detail || ""
  }));
  
  const [reportFile, setReportFile] = useState(null);
  const [reportPreview, setReportPreview] = useState(null);
  const [expectedReturnDate, setExpectedReturnDate] = useState(null);
  const [returnDateError, setReturnDateError] = useState(null);
  useEffect(() => {
    if (!open) return;
    setLocalForm({
      start_date: leaveRequestForm.start_date || "",
      end_date: leaveRequestForm.end_date || "",
      return_date: leaveRequestForm.return_date || "",
      reason_type: leaveRequestForm.reason_type || "",
      reason_detail: leaveRequestForm.reason_detail || ""
    });
    if (!leaveRequestForm.report_file) {
      setReportFile(null);
      setReportPreview(null);
    } else if (leaveRequestForm.report_file !== reportPreview) {
      setReportPreview(leaveRequestForm.report_file);
    }
    setSubstituteReason(leaveRequestForm.substitute_change_reason || "");
  }, [open, leaveRequestForm, reportPreview]);

  // Bitiş tarihine göre zorunlu işe başlama tarihini belirle
  useEffect(() => {
    if (!leaveRequestForm.end_date) {
      setExpectedReturnDate(null);
      setReturnDateError(null);
      return;
    }
    const expectedReturn = getNextWorkday(leaveRequestForm.end_date);
    setExpectedReturnDate(expectedReturn || null);
    if (expectedReturn) {
      if (!leaveRequestForm.return_date) {
        // Otomatik ilk değer
        setLocalForm(prev => ({ ...prev, return_date: expectedReturn }));
        syncToParent('return_date', expectedReturn);
        setReturnDateError(null);
      } else if (leaveRequestForm.return_date !== expectedReturn) {
        setReturnDateError(`İşe başlama tarihi ${expectedReturn} olmalı.`);
      } else {
        setReturnDateError(null);
      }
    }
  }, [leaveRequestForm.end_date, leaveRequestForm.return_date, currentPerson?.is_civil_servant]);
  const [substituteOpen, setSubstituteOpen] = useState(false);
  const [usePerParkSubstitute, setUsePerParkSubstitute] = useState(false);
  const [delegationScope, setDelegationScope] = useState(leaveRequestForm.delegation_scope || "ALL");
  const [substituteReason, setSubstituteReason] = useState(leaveRequestForm.substitute_change_reason || "");
  const [substituteEligibilityMap, setSubstituteEligibilityMap] = useState({});
  const [substituteEligibilityLoading, setSubstituteEligibilityLoading] = useState(false);
  // Sunucudan gelen uygun vekil adayları. `/api/personnel` Paket D kapsamıyla
  // park görevlisi/çim ekibi gibi rollere yalnız kendi kaydını döndürdüğü için
  // istemci peer listesini kuramıyordu; adaylar ayrı uçtan alınır.
  // null = henüz yüklenmedi (fallback istemci-türevli listeye düşer).
  const [substituteCandidates, setSubstituteCandidates] = useState(null);

  useEffect(() => {
    if (!open || !currentPerson?.id) {
      setSubstituteCandidates(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiClient.get("/substitute-candidates", { timeout: 6000 });
        if (!cancelled) {
          setSubstituteCandidates(Array.isArray(resp.data) ? resp.data : []);
        }
      } catch {
        // Eski backend / hata: istemci-türevli fallback devrede kalsın.
        if (!cancelled) setSubstituteCandidates(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open, currentPerson?.id]);

  // Local form değişikliklerini parent'a sync et
  const syncToParent = useCallback((field, value) => {
    setLeaveRequestForm(prev => ({ ...prev, [field]: value }));
  }, [setLeaveRequestForm]);

  // Tarih değişikliği - hemen parent'a bildir (calculateLeaveDays için)
  const handleDateChange = useCallback((field, value) => {
    setLocalForm(prev => ({ ...prev, [field]: value }));
    syncToParent(field, value);
  }, [syncToParent]);

  const handleRangeSelect = useCallback(({ start, end }) => {
    handleDateChange("start_date", start);
    handleDateChange("end_date", end);
  }, [handleDateChange]);

  // İzin türü değişikliği - hemen parent'a bildir
  const handleReasonTypeChange = useCallback((value) => {
    setLocalForm(prev => ({ ...prev, reason_type: value }));
    syncToParent("reason_type", value);
  }, [syncToParent]);

  // Açıklama değişikliği - sadece blur'da parent'a bildir (performans)
  const handleDetailBlur = useCallback(() => {
    if (localForm.reason_detail !== leaveRequestForm.reason_detail) {
      syncToParent("reason_detail", localForm.reason_detail);
    }
  }, [localForm.reason_detail, leaveRequestForm.reason_detail, syncToParent]);

  // Kullanıcının atanmış parkları
  const myParks = useMemo(() => {
    if (!currentPerson || !parks.length) return [];
    return parks.filter(p => 
      p.assigned_personnel_ids && 
      p.assigned_personnel_ids.includes(currentPerson.id)
    ).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [parks, currentPerson]);

  // Vekil olabilecek personelleri filtrele (İSTEMCİ-TÜREVLİ FALLBACK).
  // Yeni yol sunucudan `substituteCandidates` alır; bu memo yalnız eski
  // backend'de veya adaylar henüz yüklenmediğinde devreye girer.
  const personnelDerivedSubstitutes = useMemo(() => {
    if (!currentPerson || !personnel.length) return [];
    
    const role = currentPerson.role;
    
    // PARK_GOREVLISI ve CIM_EKIBI için: Aynı formene bağlı diğer PARK_GOREVLISI veya CIM_EKIBI
    if (role === 'park_gorevlisi' || role === 'çim_ekibi' || role === 'cim_ekibi') {
      return personnel.filter(p => 
        p.id !== currentPerson.id && 
        (p.role === 'park_gorevlisi' || p.role === 'çim_ekibi' || p.role === 'cim_ekibi') &&
        p.formen_id === currentPerson.formen_id &&
        !['passive', 'inactive', 'deleted'].includes(String(p.status || '').toLowerCase()) &&
        p.name
      ).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    }
    
    // FORMEN için: Sadece aynı MY'ye bağlı diğer FORMEN
    if (role === 'formen') {
      return personnel.filter(p => 
        p.id !== currentPerson.id && 
        p.role === 'formen' &&
        p.mudur_yardimcisi_id === currentPerson.mudur_yardimcisi_id &&
        !['passive', 'inactive', 'deleted'].includes(String(p.status || '').toLowerCase()) &&
        p.name
      ).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    }
    
    // BOLGE_MUHENDISI için: Sadece diğer BOLGE_MUHENDISI
    if (role === 'bolge_muhendisi') {
      return personnel.filter(p => 
        p.id !== currentPerson.id && 
        p.role === 'bolge_muhendisi' &&
        !['passive', 'inactive', 'deleted'].includes(String(p.status || '').toLowerCase()) &&
        p.name
      ).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    }
    
    // MUDUR_YARDIMCISI için: Sadece diğer MUDUR_YARDIMCISI
    if (role === 'mudur_yardimcisi') {
      return personnel.filter(p => 
        p.id !== currentPerson.id && 
        p.role === 'mudur_yardimcisi' &&
        !['passive', 'inactive', 'deleted'].includes(String(p.status || '').toLowerCase()) &&
        p.name
      ).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    }
    
    return [];
  }, [personnel, currentPerson]);

  // Gerçek aday listesi: sunucudan geldiyse onu kullan (park görevlisi/çim ekibi
  // gibi roller `/api/personnel` ile peer göremediği için), yoksa fallback.
  const availableSubstitutes = useMemo(() => {
    if (Array.isArray(substituteCandidates)) {
      const seen = new Set();
      return substituteCandidates
        .filter(p => p && p.id && p.name)
        .filter(p => String(p.id) !== String(currentPerson?.id))
        .filter(p => !['passive', 'inactive', 'deleted'].includes(String(p.status || '').toLowerCase()))
        .filter(p => { const k = String(p.id); if (seen.has(k)) return false; seen.add(k); return true; })
        .sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr'));
    }
    return personnelDerivedSubstitutes;
  }, [substituteCandidates, personnelDerivedSubstitutes, currentPerson?.id]);

  // Seçili vekil personeli bul (aday listesinde ara, sonra personel listesinde).
  const selectedSubstitute = useMemo(() => {
    if (!leaveRequestForm.substitute_personnel_id) return null;
    const id = String(leaveRequestForm.substitute_personnel_id);
    return availableSubstitutes.find(p => String(p.id) === id)
      || personnel.find(p => String(p.id) === id)
      || null;
  }, [leaveRequestForm.substitute_personnel_id, availableSubstitutes, personnel]);

  // ORTAK helper (leaveUtils) — edit ekranı (Vekil Düzenle) ile TEK kaynak.
  const availableSubstitutesWithEligibility = useMemo(
    () => annotateSubstituteEligibility(availableSubstitutes, substituteEligibilityMap),
    [availableSubstitutes, substituteEligibilityMap]
  );

  useEffect(() => {
    const loadSubstituteEligibilityPreview = async () => {
      if (!open || !leaveRequestForm.start_date || !leaveRequestForm.end_date || availableSubstitutes.length === 0) {
        setSubstituteEligibilityMap({});
        return;
      }
      try {
        setSubstituteEligibilityLoading(true);
        const idsCsv = availableSubstitutes.map((p) => p.id).filter(Boolean).join(",");
        const resp = await apiClient.get("/substitute-eligibility-preview", {
          timeout: 6000,
          params: {
            start_date: leaveRequestForm.start_date,
            end_date: leaveRequestForm.end_date,
            candidate_ids: idsCsv,
            exclude_personnel_id: currentPerson?.id || undefined,
          },
        });
        const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
        const nextMap = {};
        items.forEach((it) => {
          const pid = String(it?.personnel_id || "");
          if (!pid) return;
          nextMap[pid] = it;
        });
        setSubstituteEligibilityMap(nextMap);
      } catch {
        setSubstituteEligibilityMap({});
      } finally {
        setSubstituteEligibilityLoading(false);
      }
    };
    loadSubstituteEligibilityPreview();
  }, [
    open,
    leaveRequestForm.start_date,
    leaveRequestForm.end_date,
    availableSubstitutes,
    currentPerson?.id
  ]);

  const selectedSubstituteUnavailable = useMemo(() => {
    if (!leaveRequestForm.substitute_personnel_id) return false;
    const found = availableSubstitutesWithEligibility.find(
      (p) => String(p.id) === String(leaveRequestForm.substitute_personnel_id)
    );
    return Boolean(found?.isUnavailableForRange);
  }, [leaveRequestForm.substitute_personnel_id, availableSubstitutesWithEligibility]);

  const hasUnavailablePerParkSelection = useMemo(() => {
    const selectedIds = (leaveRequestForm.park_substitutes || []).map((ps) => ps?.substitute_id).filter(Boolean);
    if (selectedIds.length === 0) return false;
    const selectedSet = new Set(selectedIds.map(String));
    return availableSubstitutesWithEligibility.some(
      (p) => selectedSet.has(String(p.id)) && p.isUnavailableForRange
    );
  }, [leaveRequestForm.park_substitutes, availableSubstitutesWithEligibility]);

  // Park bazlı vekil seçimlerini initialize et
  const initializePerParkSubstitutes = () => {
    const parkSubstitutes = myParks.map(park => ({
      park_id: park.id,
      park_name: park.name,
      substitute_id: null,
      substitute_name: null
    }));
    setLeaveRequestForm({
      ...leaveRequestForm,
      park_substitutes: parkSubstitutes,
      substitute_personnel_id: null
    });
    setUsePerParkSubstitute(true);
  };

  // Tek vekil moduna geç
  const switchToSingleSubstitute = () => {
    setLeaveRequestForm({
      ...leaveRequestForm,
      park_substitutes: null,
      substitute_personnel_id: null
    });
    setUsePerParkSubstitute(false);
  };

  // Park için vekil seç — UPSERT + functional update.
  // Önceden yalnız MEVCUT park_substitutes girdilerini .map ile güncelliyordu; park
  // girdisi henüz yoksa (per-park initialize edilmemiş / tarih değişikliğinden sonra
  // liste boş kaldıysa) seçim YAPIŞMIYORDU. Artık girdi yoksa eklenir, varsa
  // güncellenir; ayrıca setLeaveRequestForm(prev => …) ile stale closure'a dayanıklı.
  const handleParkSubstituteChange = (parkId, substituteId) => {
    const substitute = availableSubstitutes.find(p => String(p.id) === String(substituteId))
      || personnel.find(p => p.id === substituteId);
    const parkObj = (myParks || []).find(p => p.id === parkId);
    setLeaveRequestForm(prev => {
      const list = Array.isArray(prev.park_substitutes) ? [...prev.park_substitutes] : [];
      const idx = list.findIndex(ps => ps.park_id === parkId);
      const base = idx >= 0 ? list[idx] : { park_id: parkId, park_name: parkObj?.name || "" };
      const entry = {
        ...base,
        park_id: parkId,
        park_name: base.park_name || parkObj?.name || "",
        substitute_id: substituteId,
        substitute_name: substitute?.name || null,
      };
      if (idx >= 0) list[idx] = entry;
      else list.push(entry);
      return { ...prev, park_substitutes: list };
    });
  };

  useEffect(() => {
    if (!leaveRequestForm.substitute_personnel_id && substituteReason) {
      setSubstituteReason("");
      syncToParent("substitute_change_reason", "");
    }
  }, [leaveRequestForm.substitute_personnel_id, substituteReason, syncToParent]);

  // Sosyal izin validasyonu
  const socialLeaveValidation = useMemo(() => {
    if (leaveRequestForm.reason_type !== 'Sosyal İzin' || !leaveRequestForm.start_date || !leaveRequestForm.end_date) {
      return { valid: true, error: null };
    }
    
    const calc = calculateLeaveDays(
      leaveRequestForm.start_date,
      leaveRequestForm.end_date,
      false,
      leaveRequestForm.return_date || null
    );
    const leaveDaysUsed = calc.workdays;
    
    // Kullanıcının mevcut sosyal izin durumu
    const usedSocialLeave = currentPerson?.used_social_leave_days || 0;
    const socialLeaveByMonth = currentPerson?.social_leave_by_month || {};
    const leaveMonth = leaveRequestForm.start_date.substring(0, 7); // "YYYY-MM"
    const monthUsage = socialLeaveByMonth[leaveMonth] || 0;
    
    // 3+ gün kontrolü
    if (leaveDaysUsed >= 3) {
      return { valid: false, error: "Sosyal izin ayda 3+ gün kullanamazsın" };
    }
    
    // Aylık limit kontrolü (max 2 gün/ay)
    if (monthUsage + leaveDaysUsed > 2) {
      const remaining = 2 - monthUsage;
      if (remaining <= 0) {
        return { valid: false, error: "Bu ay için sosyal izin hakkınız kalmadı" };
      }
      return { valid: false, error: `Bu ay için ${remaining} gün sosyal izin hakkınız kaldı` };
    }
    
    // Yıllık limit kontrolü (max 12 gün)
    if (usedSocialLeave >= 12) {
      return { valid: false, error: "Yıllık sosyal izin hakkınız kalmadı" };
    }
    
    if (usedSocialLeave + leaveDaysUsed > 12) {
      const remaining = 12 - usedSocialLeave;
      return { valid: false, error: `Yıllık ${remaining} gün sosyal izin hakkınız kaldı` };
    }
    
    return { valid: true, error: null };
  }, [leaveRequestForm.reason_type, leaveRequestForm.start_date, leaveRequestForm.end_date, leaveRequestForm.return_date, currentPerson, calculateLeaveDays]);

  // Yıllık izin 2-GÜN KURALI (planlama için önceden bildirim). Backend'le aynı üç dilim:
  //  - başlangıç 2+ gün sonra: normal
  //  - 1 gün kala (yarın): NORMAL akış için yetersiz → engelle + uyar (kartta)
  //  - aynı gün / geçmiş: izin verilir ama özel değerlendirmeye gider (bilgi notu)
  const annualNoticeValidation = useMemo(() => {
    if (leaveRequestForm.reason_type !== 'Yıllık İzin' || !leaveRequestForm.start_date) {
      return { valid: true, error: null, info: null };
    }
    const [sy, sm, sd] = String(leaveRequestForm.start_date).split('-').map(Number);
    if (!sy || !sm || !sd) return { valid: true, error: null, info: null };
    const start = new Date(sy, sm - 1, sd);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.round((start.getTime() - today.getTime()) / 86400000);
    const fmt = (d) => `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    if (days === 1) {
      const earliest = new Date(today);
      earliest.setDate(earliest.getDate() + 2);
      return {
        valid: false,
        error: `Planlama yapılabilmesi için yıllık izin başvuruları en geç 2 gün önce yapılmalıdır. En erken ${fmt(earliest)} başlangıçlı izin talep edebilirsiniz.`,
        info: null,
      };
    }
    if (days <= 0) {
      return {
        valid: true,
        error: null,
        info: 'Başlangıç bugün veya geçmiş tarihli olduğu için bu talep yönetim tarafından özel değerlendirmeye alınacaktır.',
      };
    }
    return { valid: true, error: null, info: null };
  }, [leaveRequestForm.reason_type, leaveRequestForm.start_date]);

  // Vekalet tarihi çakışması (Seçenek A — bağlayıcı kural):
  //   Yıllık İzin = BLOCK, Sosyal İzin = BLOCK, Rapor = ALLOW,
  //   Diğer = ALLOW ama sarı uyarı + açıklama zorunlu (submit'te ayrıca enforce edilir).
  const vekaletOverlapValidation = useMemo(() => {
    const start = (leaveRequestForm.start_date || "").slice(0, 10);
    const end = (leaveRequestForm.end_date || "").slice(0, 10);
    if (!start || !end || asSubstitutePeriods.length === 0) return { valid: true, error: null, warning: null };
    let conflictRange = null;
    for (const sub of asSubstitutePeriods) {
      const subStart = (sub.start_date || "").slice(0, 10);
      const subEnd = (sub.end_date || sub.return_date || "").slice(0, 10);
      if (!subStart || !subEnd) continue;
      if (start <= subEnd && subStart <= end) {
        conflictRange = `${subStart} - ${subEnd}`;
        break;
      }
    }
    // Kural tek noktada (leaveUtils.evaluateSubstitutionLeaveConflict): Yıllık/Sosyal
    // BLOCK, Rapor ALLOW, Diğer ALLOW+uyarı. Açıklama zorunluluğu submit disable'ında.
    return evaluateSubstitutionLeaveConflict(leaveRequestForm.reason_type, conflictRange);
  }, [leaveRequestForm.start_date, leaveRequestForm.end_date, leaveRequestForm.reason_type, asSubstitutePeriods]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Dosya boyutu kontrolü (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Dosya boyutu 5MB\'dan küçük olmalıdır');
        return;
      }

      // Dosya tipini kontrol et
      if (!file.type.startsWith('image/')) {
        alert('Lütfen sadece resim dosyası yükleyin');
        return;
      }

      setReportFile(file);

      // Önizleme için base64'e çevir
      const reader = new FileReader();
      reader.onloadend = () => {
        setReportPreview(reader.result);
        // Form'a base64'i ekle
        setLeaveRequestForm({
          ...leaveRequestForm,
          report_file: reader.result,
          report_filename: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setReportFile(null);
    setReportPreview(null);
    setLeaveRequestForm({
      ...leaveRequestForm,
      report_file: null,
      report_filename: null
    });
  };
  return (
    <DraggableModal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      closeOnOverlayClick={false}
      title={<span className="flex items-center gap-2"><CalendarIcon className="h-5 w-5 text-blue-600" />İzin Talebi Oluştur</span>}
      subtitle="İzin talebiniz onay için yöneticinize iletilecektir"
      maxWidth="max-w-md"
    >
        <form onSubmit={handleSubmit} className="space-y-4">
          <DateRangePicker
            id="leave-date-range"
            label="Başlangıç - Bitiş Tarihi"
            required
            startDate={localForm.start_date}
            endDate={localForm.end_date}
            onChange={handleRangeSelect}
            helperText="Seçtiğiniz tarih aralığı takvimde daha belirgin renkte görünür."
            highlightHolidays
          />

          {/* İşe Başlama Tarihi - Manuel */}
          <div>
            <DatePicker
              id="return-date"
              label="İşe Başlama Tarihi"
              required
              value={localForm.return_date}
              onChange={(value) => handleDateChange('return_date', value)}
              error={returnDateError}
              helperText="İzinden döndükten sonra işe başlayacağınız ilk gün"
              highlightHolidays
            />
            {expectedReturnDate && !returnDateError && (
              <p className="text-xs text-blue-600 mt-1">
                İşe başlama tarihi: {formatTRDate(expectedReturnDate)}
              </p>
            )}
          </div>

          {/* İzin Hesaplama Özeti */}
          {leaveRequestForm.start_date && leaveRequestForm.end_date && (() => {
            const calc = calculateLeaveDays(
              leaveRequestForm.start_date,
              leaveRequestForm.end_date,
              Boolean(currentPerson?.is_civil_servant),
              leaveRequestForm.return_date || null
            );
            return calc.total > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-blue-900 text-sm">
                  📊 İzin Özeti
                  {currentPerson?.is_civil_servant && (
                    <span className="ml-2 text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                      Devlet Memuru
                    </span>
                  )}
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-white rounded p-2">
                    <p className="text-gray-600 text-xs">Toplam Gün</p>
                    <p className="font-bold text-purple-600">{calc.total} gün</p>
                  </div>
                  <div className="bg-white rounded p-2">
                    <p className="text-gray-600 text-xs">Hafta İçi</p>
                    <p className="font-bold text-green-600">{calc.weekdays} gün</p>
                  </div>
                  <div className="bg-white rounded p-2">
                    <p className="text-gray-600 text-xs">Hafta Sonu</p>
                    <p className="font-bold text-orange-600">{calc.weekends} gün</p>
                  </div>
                  <div className="bg-red-50 rounded p-2">
                    <p className="text-gray-600 text-xs">İzin Hakkından Düşecek</p>
                    <p className="font-bold text-red-600">{calc.workdays} gün</p>
                  </div>
                </div>
                {!currentPerson?.is_civil_servant && (
                  <p className="text-xs text-gray-600 mt-2">
                    💡 Hafta sonları ve resmi tatiller izin hakkından düşülmez
                  </p>
                )}
              </div>
            );
          })()}
          <div>
            <Label htmlFor="reason-type">İzin Türü *</Label>
            <SearchableSelect
              value={localForm.reason_type}
              onChange={handleReasonTypeChange}
              showAllOption={false}
              placeholder="İzin türünü seçin..."
              searchPlaceholder="İzin türü ara..."
              className={!localForm.reason_type ? "border-yellow-400 bg-yellow-50" : ""}
              options={[
                { id: "Yıllık İzin", name: "Yıllık İzin" },
                { id: "Sosyal İzin", name: "Sosyal İzin" },
                { id: "Rapor", name: "Rapor" },
                { id: "Diğer", name: "Diğer" },
              ]}
            />
          </div>

          {/* Sosyal İzin Uyarısı */}
          {!socialLeaveValidation.valid && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-700">Sosyal İzin Uyarısı</p>
                <p className="text-sm text-red-600">{socialLeaveValidation.error}</p>
              </div>
            </div>
          )}

          {/* Yıllık İzin 2-gün kuralı uyarısı (kartın içinde, submit'i engeller) */}
          {!annualNoticeValidation.valid && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-700">Yıllık İzin Bildirim Süresi</p>
                <p className="text-sm text-red-600">{annualNoticeValidation.error}</p>
              </div>
            </div>
          )}
          {annualNoticeValidation.valid && annualNoticeValidation.info && (
            <div className="bg-blue-50 border border-blue-300 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">{annualNoticeValidation.info}</p>
            </div>
          )}

          {/* Rapor Yükleme Alanı - Sadece "Rapor" seçildiğinde */}
          {localForm.reason_type === "Rapor" && (
            <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50">
              <Label className="text-blue-900 font-semibold mb-2 block">
                Rapor Fotoğrafı Yükle *
              </Label>
              {!reportPreview ? (
                <div className="text-center">
                  <label
                    htmlFor="report-file"
                    className="cursor-pointer inline-flex items-center gap-2 px-4 py-3 bg-white border-2 border-blue-400 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <Upload className="h-5 w-5 text-blue-600" />
                    <span className="text-blue-900 font-medium">Dosya Seç</span>
                  </label>
                  <input
                    id="report-file"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <p className="text-xs text-blue-700 mt-2">
                    JPG, PNG veya JPEG (Max 5MB)
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <img
                      src={reportPreview}
                      alt="Rapor önizleme"
                      className="w-full h-48 object-cover rounded-lg border-2 border-blue-300"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2"
                      onClick={removeFile}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-800 bg-white p-2 rounded">
                    <FileText className="h-4 w-4" />
                    <span className="truncate">{reportFile?.name}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Yerime Bakacak Kişi Seçimi - Park Görevlileri, Çim Ekibi, Formen, BM, MY için */}
          {['park_gorevlisi', 'çim_ekibi', 'cim_ekibi', 'formen', 'bolge_muhendisi', 'mudur_yardimcisi'].includes(currentPerson?.role) && availableSubstitutes.length > 0 && (
            <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
              <Label className="text-green-900 font-semibold mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Yerime Bakacak Kişi (Opsiyonel)
              </Label>
              <p className="text-xs text-green-700 mb-3">
                {['park_gorevlisi', 'çim_ekibi', 'cim_ekibi'].includes(currentPerson?.role) 
                  ? "İzin sürenizde parklarınıza bakacak kişiyi seçebilirsiniz"
                  : "İzin sürenizde yerinize bakacak kişiyi seçebilirsiniz"}
              </p>
              
              {/* Birden fazla park varsa seçenek sun - Sadece park görevlileri için */}
              {['park_gorevlisi', 'çim_ekibi', 'cim_ekibi'].includes(currentPerson?.role) && myParks.length > 1 && (
                <div className="flex gap-2 mb-3">
                  <Button
                    type="button"
                    size="sm"
                    variant={!usePerParkSubstitute ? "default" : "outline"}
                    onClick={switchToSingleSubstitute}
                    className="text-xs"
                  >
                    Tüm parklara aynı vekil
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={usePerParkSubstitute ? "default" : "outline"}
                    onClick={initializePerParkSubstitutes}
                    className="text-xs"
                  >
                    Her parka farklı vekil
                  </Button>
                </div>
              )}

              {/* Tek vekil seçimi - Tüm roller için */}
              {(!usePerParkSubstitute || !['park_gorevlisi', 'çim_ekibi', 'cim_ekibi'].includes(currentPerson?.role)) && (
                <>
                  <Popover open={substituteOpen} onOpenChange={setSubstituteOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between bg-white"
                        type="button"
                      >
                        {selectedSubstitute ? (
                          <span className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-green-600" />
                            {selectedSubstitute.name}
                          </span>
                        ) : (
                          <span className="text-gray-500">Vekil personel seçin...</span>
                        )}
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Personel ara..." />
                        <CommandList>
                          <CommandEmpty>Personel bulunamadı</CommandEmpty>
                          <CommandGroup>
                            {selectedSubstitute && (
                              <CommandItem
                                onSelect={() => {
                                  setLeaveRequestForm({
                                    ...leaveRequestForm,
                                    substitute_personnel_id: null
                                  });
                                  setSubstituteOpen(false);
                                }}
                                className="text-red-600"
                              >
                                <X className="mr-2 h-4 w-4" />
                                Seçimi Temizle
                              </CommandItem>
                            )}
                            {availableSubstitutesWithEligibility.map((person) => (
                              <CommandItem
                                key={person.id}
                                value={person.name}
                                disabled={person.isUnavailableForRange}
                                onSelect={() => {
                                  if (person.isUnavailableForRange) return;
                                  const updatedForm = {
                                    ...leaveRequestForm,
                                    substitute_personnel_id: person.id
                                  };
                                  // BM ve MY için delegation_scope'u da set et
                                  if (['bolge_muhendisi', 'mudur_yardimcisi', 'formen'].includes(currentPerson?.role)) {
                                    updatedForm.delegation_scope = delegationScope || "ALL";
                                  }
                                  setLeaveRequestForm(updatedForm);
                                  setSubstituteOpen(false);
                                }}
                                className={person.isUnavailableForRange ? "opacity-50 cursor-not-allowed" : ""}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    leaveRequestForm.substitute_personnel_id === person.id
                                      ? "opacity-100 text-green-600"
                                      : "opacity-0"
                                  }`}
                                />
                                <div className="flex flex-col">
                                  <span>{person.name}</span>
                                  {person.isUnavailableForRange && (
                                    <span className="text-[11px] text-red-600">
                                      {person.unavailableText || "Bu tarihte uygun değil"}
                                    </span>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  {selectedSubstitute && (
                    <div className="mt-3 p-2 bg-white rounded border border-green-300">
                      <p className="text-sm text-green-800">
                        ✅ <strong>{selectedSubstitute.name}</strong> izin sürenizde {myParks.length > 1 ? `${myParks.length} parkınıza` : 'parkınıza'} bakacak.
                      </p>
                    </div>
                  )}
                  {selectedSubstituteUnavailable && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-300 rounded">
                      <p className="text-xs text-red-700">
                        Seçili vekil bu tarih aralığında izinde/raporlu. Lütfen başka personel seçin.
                      </p>
                    </div>
                  )}
                  {substituteEligibilityLoading && (
                    <p className="text-xs text-gray-600 mt-2">Uygunluk kontrol ediliyor...</p>
                  )}

                  {selectedSubstitute && ['formen', 'bolge_muhendisi', 'mudur_yardimcisi'].includes(currentPerson?.role) && (
                    <div className="mt-3">
                      <Label className="text-xs font-medium text-green-800 mb-1 block">
                        Vekil Açıklaması (Opsiyonel)
                      </Label>
                      <Textarea
                        value={substituteReason}
                        onChange={(e) => {
                          setSubstituteReason(e.target.value);
                          syncToParent("substitute_change_reason", e.target.value);
                        }}
                        placeholder="Örn: İş dağılımı, bölge yoğunluğu..."
                        className="bg-white"
                        rows={2}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Park bazlı vekil seçimi - Sadece park görevlileri için */}
              {['park_gorevlisi', 'çim_ekibi', 'cim_ekibi'].includes(currentPerson?.role) && usePerParkSubstitute && (
                <div className="space-y-3">
                  {myParks.map(park => {
                    const parkSub = (leaveRequestForm.park_substitutes || []).find(ps => ps.park_id === park.id);
                    const selectedSub = parkSub?.substitute_id ? personnel.find(p => p.id === parkSub.substitute_id) : null;
                    
                    return (
                      <div key={park.id} className="bg-white rounded-lg p-3 border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <TreePine className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-sm text-green-800">{park.name}</span>
                        </div>
                        <SearchableSelect
                          value={parkSub?.substitute_id || ""}
                          onChange={(v) => handleParkSubstituteChange(park.id, v || null)}
                          allValue=""
                          allLabel="Vekil seçin..."
                          searchPlaceholder="Personel ara..."
                          className="w-full text-sm"
                          options={availableSubstitutesWithEligibility.map((p) => ({
                            id: p.id,
                            name: `${p.name}${p.isUnavailableForRange ? ` (${p.unavailableText || "uygun değil"})` : ""}`,
                            disabled: p.isUnavailableForRange,
                            title: p.isUnavailableForRange ? (p.unavailableText || "uygun değil") : undefined,
                          }))}
                        />
                        {selectedSub && (
                          <p className="text-xs text-green-600 mt-1">
                            ✅ {selectedSub.name} bakacak
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Yetkiler Seçimi - Formen, BM, MY için */}
          {['formen', 'bolge_muhendisi', 'mudur_yardimcisi'].includes(currentPerson?.role) && (
            <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
              <Label className="text-blue-900 font-semibold mb-2 block">
                Yetkiler
              </Label>
              <SearchableSelect
                value={delegationScope}
                onChange={(value) => {
                  setDelegationScope(value);
                  setLeaveRequestForm(prev => ({ ...prev, delegation_scope: value }));
                }}
                disabled={!leaveRequestForm.substitute_personnel_id}
                showAllOption={false}
                placeholder={leaveRequestForm.substitute_personnel_id ? "Yetkiler seçin" : "Önce vekil personel seçin"}
                searchPlaceholder="Yetki ara..."
                className="w-full bg-white"
                options={[
                  { id: "ALL", name: "Tüm Yetkiler" },
                  { id: "LEAVE_APPROVAL", name: "İzin Onayı Yetkileri" },
                  { id: "TASK_APPROVAL", name: "Görev Onayı Yetkileri" },
                ]}
              />
              <p className="text-xs text-blue-700 mt-2">
                {leaveRequestForm.substitute_personnel_id 
                  ? "Vekil personelin hangi yetkileri kullanabileceğini seçin"
                  : "Vekil personel seçtiğinizde yetkiler seçebilirsiniz"}
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="reason-detail">
              Açıklama {localForm.reason_type === 'Diğer' ? '*' : '(Opsiyonel)'}
            </Label>
            <Textarea
              id="reason-detail"
              value={localForm.reason_detail}
              onChange={(e) => setLocalForm(prev => ({ ...prev, reason_detail: e.target.value }))}
              onBlur={handleDetailBlur}
              placeholder={localForm.reason_type === 'Diğer' 
                ? "Diğer izin türü için açıklama zorunludur..." 
                : "İzin talebiniz hakkında detaylı bilgi verebilirsiniz..."}
              rows={3}
              required={localForm.reason_type === 'Diğer'}
              className={localForm.reason_type === 'Diğer' && !localForm.reason_detail?.trim() ? "border-red-400 bg-red-50" : ""}
            />
            {localForm.reason_type === 'Diğer' && !localForm.reason_detail?.trim() && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Diğer izin türü için açıklama zorunludur
              </p>
            )}
          </div>
          
          {/* İzin türü seçilmedi uyarısı */}
          {!localForm.reason_type && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-700">Lütfen izin türünü seçin</p>
            </div>
          )}

          {errorMessage && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          {!vekaletOverlapValidation.valid && vekaletOverlapValidation.error && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{vekaletOverlapValidation.error}</p>
            </div>
          )}

          {vekaletOverlapValidation.valid && vekaletOverlapValidation.warning && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{vekaletOverlapValidation.warning}</p>
            </div>
          )}

          {hasUnavailablePerParkSelection && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">
                Park vekillerinden en az biri izinli/raporlu. Lütfen uygun personel seçin.
              </p>
            </div>
          )}
          
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              İptal
            </Button>
            <Button 
              type="submit" 
              disabled={isReadOnly || loading || !!returnDateError || !socialLeaveValidation.valid || !annualNoticeValidation.valid || !vekaletOverlapValidation.valid || !localForm.reason_type || (localForm.reason_type === 'Diğer' && !localForm.reason_detail?.trim()) || selectedSubstituteUnavailable || hasUnavailablePerParkSelection}
              className={((!!returnDateError) || !socialLeaveValidation.valid || !annualNoticeValidation.valid || !vekaletOverlapValidation.valid || !localForm.reason_type || (localForm.reason_type === 'Diğer' && !localForm.reason_detail?.trim()) || selectedSubstituteUnavailable || hasUnavailablePerParkSelection) ? "opacity-50 cursor-not-allowed" : ""}
            >
              {isReadOnly ? "Salt Okunur Mod" : loading ? "Gönderiliyor..." : "Talep Oluştur"}
            </Button>
          </div>
        </form>
    </DraggableModal>
  );
};

export default LeaveRequestFormDialog;
